import { diatonicIndex, fromDiatonicIndex, p, type SpelledPitch } from './pitch.ts';

export const CLEFS = ['treble', 'bass', 'alto', 'tenor'] as const;
export type Clef = (typeof CLEFS)[number];

export function isClef(value: string): value is Clef {
  return (CLEFS as readonly string[]).includes(value);
}

export interface ClefInfo {
  id: Clef;
  name: string;
  /** Natural pitch on the bottom (first) staff line. */
  bottomLine: SpelledPitch;
  /** The line the clef symbol "names", used as a reading landmark. */
  landmark: { pitch: SpelledPitch; line: number; description: string };
  /**
   * Written range this product allows for practice in this clef: anything that
   * needs at most three ledger lines (down to the space below the third ledger
   * line under the staff, up to the space above the third ledger line above it).
   */
  practicalRange: { low: SpelledPitch; high: SpelledPitch };
  /** Comfortable default range used for new teacher activities. */
  defaultRange: { low: SpelledPitch; high: SpelledPitch };
}

/**
 * Staff geometry. Position 0 is the bottom line, 8 is the top line.
 * Even positions are lines, odd positions are spaces.
 */
export const STAFF_BOTTOM_LINE = 0;
export const STAFF_TOP_LINE = 8;
export const MAX_LEDGER_LINES = 3;

export const CLEF_INFO: Record<Clef, ClefInfo> = {
  treble: {
    id: 'treble',
    name: 'Treble clef',
    bottomLine: p('E4'),
    landmark: { pitch: p('G4'), line: 2, description: 'The treble (G) clef curls around the second line, which is G4.' },
    practicalRange: { low: p('E3'), high: p('F6') },
    defaultRange: { low: p('C4'), high: p('A5') },
  },
  bass: {
    id: 'bass',
    name: 'Bass clef',
    bottomLine: p('G2'),
    landmark: { pitch: p('F3'), line: 4, description: 'The bass (F) clef’s two dots surround the fourth line, which is F3.' },
    practicalRange: { low: p('G1'), high: p('A4') },
    defaultRange: { low: p('E2'), high: p('C4') },
  },
  alto: {
    id: 'alto',
    name: 'Alto clef',
    bottomLine: p('F3'),
    landmark: { pitch: p('C4'), line: 3, description: 'The alto (C) clef points to the middle line, which is middle C (C4).' },
    practicalRange: { low: p('F2'), high: p('G5') },
    defaultRange: { low: p('C3'), high: p('G4') },
  },
  tenor: {
    id: 'tenor',
    name: 'Tenor clef',
    bottomLine: p('D3'),
    landmark: { pitch: p('C4'), line: 4, description: 'The tenor (C) clef points to the fourth line, which is middle C (C4).' },
    practicalRange: { low: p('D2'), high: p('E5') },
    defaultRange: { low: p('A2'), high: p('E4') },
  },
};

/** Diatonic steps above the bottom staff line (0 = bottom line, 8 = top line). */
export function staffPosition(clef: Clef, pitchValue: Pick<SpelledPitch, 'letter' | 'octave'>): number {
  return diatonicIndex(pitchValue) - diatonicIndex(CLEF_INFO[clef].bottomLine);
}

/** Natural pitch written at a staff position in a clef. */
export function pitchAtPosition(clef: Clef, position: number): SpelledPitch {
  return fromDiatonicIndex(diatonicIndex(CLEF_INFO[clef].bottomLine) + position);
}

/**
 * Grand staff: a treble staff above a bass staff, joined by a brace (how piano
 * music is written). It is a way of showing treble and bass notes together, not
 * a clef of its own — every note still belongs to one of the two staves.
 */
export const GRAND_STAFF_CLEFS = ['treble', 'bass'] as const satisfies readonly Clef[];
export type GrandStaffClef = (typeof GRAND_STAFF_CLEFS)[number];

export function isGrandStaffClef(clef: Clef): clef is GrandStaffClef {
  return clef === 'treble' || clef === 'bass';
}

/** Staff a note is written on in a grand staff: middle C and above on the treble staff, below it on the bass staff. */
export function grandStaffClefFor(pitchValue: Pick<SpelledPitch, 'letter' | 'octave'>): GrandStaffClef {
  return diatonicIndex(pitchValue) >= diatonicIndex(p('C4')) ? 'treble' : 'bass';
}

export const GRAND_STAFF_INFO = {
  name: 'Grand staff',
  /** Lowest bass-staff note to highest treble-staff note, each within three ledger lines. */
  practicalRange: { low: CLEF_INFO.bass.practicalRange.low, high: CLEF_INFO.treble.practicalRange.high },
  /** Bottom line of the bass staff to the top line of the treble staff. */
  defaultRange: { low: p('G2'), high: p('F5') },
} as const;

export function isLinePosition(position: number): boolean {
  return ((position % 2) + 2) % 2 === 0;
}

/** Number of ledger lines needed to write a note at a staff position. */
export function ledgerLineCount(position: number): number {
  if (position < STAFF_BOTTOM_LINE) return Math.floor(-position / 2);
  if (position > STAFF_TOP_LINE) return Math.floor((position - STAFF_TOP_LINE) / 2);
  return 0;
}

export type StaffRegion = 'staff' | 'ledger-below' | 'ledger-above';

/**
 * Region of a staff position. The space directly below the bottom line (-1) and
 * directly above the top line (9) need no ledger lines and count as "staff".
 */
export function staffRegion(position: number): StaffRegion {
  if (position < -1) return 'ledger-below';
  if (position > STAFF_TOP_LINE + 1) return 'ledger-above';
  return 'staff';
}

export interface PositionDescription {
  position: number;
  kind: 'line' | 'space';
  region: StaffRegion;
  /** 1–5 for staff lines, counted from the bottom. */
  lineNumber?: number;
  /** 1–4 for staff spaces, counted from the bottom. */
  spaceNumber?: number;
  ledgerLines: number;
  /** Plain-language description, e.g. "the second line" or "two ledger lines below the staff". */
  text: string;
}

const ORDINAL = ['first', 'second', 'third', 'fourth', 'fifth'];
const COUNT_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'];

export function describePosition(position: number): PositionDescription {
  const line = isLinePosition(position);
  const ledgers = ledgerLineCount(position);
  const region = staffRegion(position);
  const base = { position, kind: line ? 'line' : 'space', region, ledgerLines: ledgers } as const;

  if (position >= STAFF_BOTTOM_LINE && position <= STAFF_TOP_LINE) {
    if (line) {
      const lineNumber = position / 2 + 1;
      return { ...base, lineNumber, text: `the ${ORDINAL[lineNumber - 1]} line` };
    }
    const spaceNumber = (position + 1) / 2;
    return { ...base, spaceNumber, text: `the ${ORDINAL[spaceNumber - 1]} space` };
  }
  if (position === -1) return { ...base, text: 'the space just below the staff' };
  if (position === STAFF_TOP_LINE + 1) return { ...base, text: 'the space just above the staff' };

  const where = position < 0 ? 'below' : 'above';
  if (line) {
    return { ...base, text: `${ordinalLedger(ledgers)} ledger line ${where} the staff` };
  }
  return { ...base, text: `the space ${where} ${ordinalLedger(ledgers)} ledger line ${where} the staff` };
}

function ordinalLedger(n: number): string {
  return ORDINAL[n - 1] ? `the ${ORDINAL[n - 1]}` : `ledger ${n}`;
}

/** "one ledger line", "two ledger lines" … */
export function ledgerCountText(n: number): string {
  const word = COUNT_WORD[n] ?? String(n);
  return `${word} ledger ${n === 1 ? 'line' : 'lines'}`;
}

/** Naturals on the five staff lines, bottom to top (for mnemonics/explanations). */
export function staffLineNames(clef: Clef): SpelledPitch[] {
  return [0, 2, 4, 6, 8].map((pos) => pitchAtPosition(clef, pos));
}

/** Naturals in the four staff spaces, bottom to top. */
export function staffSpaceNames(clef: Clef): SpelledPitch[] {
  return [1, 3, 5, 7].map((pos) => pitchAtPosition(clef, pos));
}

/** Staff position of each clef's landmark line (e.g. treble G line = 2). */
export function landmarkPosition(clef: Clef): number {
  return staffPosition(clef, CLEF_INFO[clef].landmark.pitch);
}

/** Middle C's staff position in a clef (useful as a shared landmark). */
export function middleCPosition(clef: Clef): number {
  return staffPosition(clef, p('C4'));
}
